/* eslint-disable no-nested-ternary */
import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import {
  DailyMetrics,
  DateRange,
  ProductsSummaryResp,
  StoreSummary,
  UseCasesSummaryResp,
} from '@/features/dashboard/reports_v2/models';
import { MetricKind } from '@/webapi/models';
import { Experience } from '@/webapi/use-experience-api';
import { getQueryDate } from '@/features/dashboard/reports/context';

interface UpliftsResp {
  experiences: Array<Experience>;
}
export type ExperienceLabels = {
  experienceLabels: {
    [key: string]: {
      labels: string[];
    };
  };
};
type LabelSummaryResp = {
  summary: [
    {
      orders: number;
      revenue: number;
      netRevenue: number;
      label: string;
    },
  ];
  total: 1;
};

export function useReportsApi() {
  const { post, get, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: false,
    },
  }));

  async function getStoreSummary(
    dateRange: DateRange,
    startDate?: Date,
    endDate?: Date,
  ): Promise<StoreSummary> {
    const { endAt, startAt } = getRange(dateRange);
    const isCustom = dateRange === DateRange.CUSTOM;
    return post(routes.storeTotals, {
      startAt: isCustom ? startDate : startAt,
      endAt: isCustom ? endDate : endAt,
    });
  }
  async function getProductsByLabel(
    page: number,
    size: number,
    args: {
      metric: MetricKind;
      experienceLabels: any;
      dateRange: DateRange;
      label: string;
    },
  ) {
    const isCustom = args.dateRange === DateRange.CUSTOM;
    const startDate = getQueryDate(`s`);
    const endDate = getQueryDate(`e`);
    if (!args.experienceLabels) {
      return Promise.reject();
    }

    return post(routes.productsByLabel(), {
      type: args.metric === MetricKind.DIRECT_REVENUE ? `direct` : `assisted`,
      page,
      size,
      metric: args.metric,
      labels: args.label
        ? args.experienceLabels?.filter((x) => x.labels?.includes(args.label))
        : args.experienceLabels,
      startAt: isCustom ? startDate : getRange(args.dateRange).startAt,
      endAt: isCustom ? endDate : getRange(args.dateRange).endAt,
    });
  }

  async function getExperiencesLabels(): Promise<ExperienceLabels> {
    return get(routes.experiencesLabels());
  }
  async function getExperiencesSummary(
    page: number,
    size: number,
    args: {
      dateRange: DateRange;
      metric: MetricKind;
      startDate: Date;
      endDate: Date;
    },
  ): Promise<UseCasesSummaryResp> {
    const { endAt, startAt } = getRange(args.dateRange);
    const isCustom = args.dateRange === DateRange.CUSTOM;
    return post(routes.experienceSummaryV2, {
      startAt: isCustom ? args.startDate : startAt,
      endAt: isCustom ? args.endDate : endAt,
      page,
      size,
      sort: args.metric,
    });
  }
  async function getStoreDailySummary(
    dateRange: DateRange,
    startDate?: Date,
    endDate?: Date,
  ): Promise<DailyMetrics> {
    const { endAt, startAt } = getRange(dateRange);
    const isCustom = dateRange === DateRange.CUSTOM;
    return post(routes.storeDailySummary, {
      startAt: isCustom ? startDate : startAt,
      endAt: isCustom ? endDate : endAt,
    });
  }

  async function getLabelsSummary(
    page: number,
    size: number,
    args: {
      dateRange: DateRange;
      metric: MetricKind;
      labels: {
        [key: string]: {
          labels: string[];
        };
      };
      startDate: Date;
      endDate: Date;
    },
  ): Promise<LabelSummaryResp> {
    const { endAt, startAt } = getRange(args.dateRange);
    const isCustom = args.dateRange === DateRange.CUSTOM;
    if (!args.labels) {
      return Promise.reject();
    }
    const promise: LabelSummaryResp = await post(routes.labelSummary, {
      startAt: isCustom ? args.startDate : startAt,
      endAt: isCustom ? args.endDate : endAt,
      labels: args.labels,
      page,
      size,
      type:
        args.metric === MetricKind.ASSISTED_REVENUE
          ? `assisted`
          : args.metric === MetricKind.REVENUE
          ? `rev_via_exp`
          : `direct`,
    });
    return {
      total: promise.total,
      summary: promise.summary.map((x) => ({
        label: x.label,
        revenue: x.revenue || 0,
        netRevenue: x.netRevenue || 0,
        orders: (x.orders || 0) * 1, // gprc serializes int64 as a string, this converts it to a number
      })),
    } as LabelSummaryResp;
  }

  async function getProductsSummary(
    page: number,
    size: number,
    args: {
      dateRange: DateRange;
      metric: MetricKind;
      startDate: Date;
      endDate: Date;
    },
  ): Promise<ProductsSummaryResp> {
    const { endAt, startAt } = getRange(args.dateRange);
    const isCustom = args.dateRange === DateRange.CUSTOM;
    return post(routes.productsSummary, {
      startAt: isCustom ? args.startDate : startAt,
      endAt: isCustom ? args.endDate : endAt,
      page,
      size,
      sort: args.metric,
    });
  }

  async function getUplifts(): Promise<UpliftsResp> {
    return post(routes.listExperiences(), {
      page: 0,
      size: 500,
      includePaused: true,
      filter: `ONE_HUNDRED_PERCENT`,
      sort: { option: `BY_PUBLISH_DATE`, direction: -1 },
      timezoneOffset: new Date().getTimezoneOffset(),
    });
  }

  async function hideUplift(id: string) {
    return post(routes.hideUplift(), { id });
  }

  return {
    loading,
    hideUplift,
    getLabelsSummary,
    getUplifts,
    getProductsSummary,
    getStoreSummary,
    getExperiencesSummary,
    getStoreDailySummary,
    getExperiencesLabels,
    getProductsByLabel,
  };
}

export function getRange(dateRange: DateRange) {
  let endAt = truncateMinutes(new Date());
  let startAt: Date;
  const oneDay = 8.64e7;
  switch (dateRange) {
    case DateRange.LAST_7_DAYS:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 7));
      break;
    case DateRange.LAST_30_DAYS:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 30));
      break;
    case DateRange.TODAY:
      startAt = endAt;
      break;
    case DateRange.YESTERDAY:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay));
      startAt.setUTCHours(0, 0, 0, 0);
      endAt = startAt;
      endAt.setUTCHours(23, 59, 59, 999);
      break;
    case DateRange.LAST_YEAR:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 365));
      break;
    case DateRange.ALL_TIME:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 365 * 100));
      break;
    case DateRange.CUSTOM:
      startAt = getQueryDate(`s`);
      endAt = getQueryDate(`e`);
      break;
    default:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 7));
  }
  return { endAt, startAt };
}
export function truncateMinutes(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    0,
    0,
    0,
  );
}
